<script setup>
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { Badge, Button, Select } from '@drama/ui'

import { useAuditText, variantOf } from '../audit/audit-text.js'

const props = defineProps({
  // 契约 NovelComparisonSide：这一边的状态、最近一回合的指标与输出、对局视图。
  side: { type: Object, required: true },
  models: { type: Array, required: true },
  modelId: { type: String, default: '' },
  // 生成中、已生成而没开新一轮、或导演对比的 B 边：模型下拉不可改。
  modelLocked: { type: Boolean, default: false },
  directorComparison: { type: Boolean, default: false },
  // 源对局属于当前运营账号时才显示「采纳这一边」。
  adoptable: { type: Boolean, default: false },
  adoptedSide: { type: String, default: null },
  busy: { type: Boolean, default: false },
  cancelling: { type: Boolean, default: false },
})
const emit = defineEmits(['update:modelId', 'retry', 'adopt', 'cancel', 'advance'])

const { t } = useI18n()
const tc = (key, params) => t(`visualGameAdmin.novelComparison.${key}`, params ?? {})
const { enumLabel, errorLabel } = useAuditText()

const view = computed(() => props.side.view)
const generating = computed(() => props.side.status === 'generating')
const options = computed(() => props.models.map((model) => ({ value: model.id, label: model.configured ? model.label : `${model.label} · ${tc('modelMissing')}` })))
// 生成中显示流式草稿（对局视图的 preview），否则是最近一回合新生成的段（失败时是失败前的草稿）。
const segments = computed(() => (generating.value ? view.value.preview?.segments ?? [] : props.side.output))
const metrics = computed(() => {
  const { elapsed_ms, first_token_ms, writing_ms, calls, output_chars } = props.side.metrics
  return {
    elapsed: elapsed_ms === null ? '—' : tc('seconds', { value: (elapsed_ms / 1000).toFixed(2) }),
    calls: calls ?? '—',
    chars: output_chars ?? '—',
    first: first_token_ms == null ? '—' : tc('seconds', { value: (first_token_ms / 1000).toFixed(2) }),
    writing: writing_ms == null ? '—' : tc('seconds', { value: (writing_ms / 1000).toFixed(2) }),
  }
})
const canAdopt = computed(() => props.side.status === 'completed' && props.side.added_turns > 0 && props.adoptedSide === null && !props.busy)

function speakerName(speakerId) {
  if (speakerId === 'narrator') return tc('narrator')
  return view.value.characters.find((person) => person.id === speakerId)?.name ?? '—'
}
</script>

<template>
  <section class="cmp-side" :data-testid="`comparison-side-${side.side}`">
    <header class="cmp-side__head">
      <span class="cmp-side__mark" aria-hidden="true">{{ side.side.toUpperCase() }}</span>
      <Select
        :model-value="modelId"
        :options="options"
        :disabled="modelLocked"
        :aria-label="tc('model', { side: side.side.toUpperCase() })"
        :title="side.model || ''"
        block
        @update:model-value="emit('update:modelId', $event)"
      />
      <Badge size="sm" :variant="variantOf(side.status)">{{ tc(`statuses.${side.status}`) }}</Badge>
    </header>
    <p v-if="directorComparison" class="cmp-side__note">{{ side.director_enabled ? tc('director.on') : tc('director.off') }}</p>
    <p v-if="directorComparison && side.director_enabled && side.status === 'completed'" class="cmp-side__note" data-testid="comparison-director-sample">{{ t(view.director?.last_turn?.writer_prompt_guidance === 'included' ? 'novelPlay.comparison.directorIncluded' : 'novelPlay.comparison.directorNotIncluded') }}</p>
    <dl class="cmp-side__metrics">
      <div><dt>{{ t('novelPlay.comparison.firstText') }}</dt><dd>{{ metrics.first }}</dd></div>
      <div><dt>{{ t('novelPlay.comparison.writingTime') }}</dt><dd>{{ metrics.writing }}</dd></div>
      <div><dt>{{ tc('metrics.elapsed') }}</dt><dd>{{ metrics.elapsed }}</dd></div>
      <div><dt>{{ tc('metrics.calls') }}</dt><dd>{{ metrics.calls }}</dd></div>
      <div><dt>{{ tc('metrics.chars') }}</dt><dd>{{ metrics.chars }}</dd></div>
      <Badge size="sm" variant="neutral">{{ tc('metrics.unbilled') }}</Badge>
    </dl>
    <p v-if="side.status === 'failed'" class="cmp-side__error" :title="side.error_code || ''">{{ errorLabel(side.error_code) }}</p>
    <p v-if="segments.length && side.status !== 'completed'" class="cmp-side__note">{{ generating ? tc('generatingHint') : tc('failedDraft') }}</p>
    <div class="au-lines cmp-side__body" aria-live="polite">
      <p v-for="(segment, index) in segments" :key="segment.segment_id || index" class="au-line" :class="`cmp-line--${segment.channel}`">
        <span class="au-line__who">{{ speakerName(segment.speaker_id) }}<small>{{ enumLabel('story.channels', segment.channel) }}</small></span>
        <span class="au-line__text">{{ segment.text }}</span>
      </p>
      <p v-if="!segments.length" class="cmp-side__note">{{ generating ? tc('statuses.generating') : tc('empty') }}</p>
    </div>
    <footer class="cmp-side__foot">
      <RouterLink class="cmp-side__link" :to="{ name: 'novel-run-audit', params: { runId: side.run_id } }">{{ tc('viewRecord') }}</RouterLink>
      <div class="cmp-side__actions">
        <Button v-if="generating" size="sm" variant="secondary" :loading="cancelling" :disabled="cancelling" @click="emit('cancel', side)">{{ t('novelPlay.comparison.cancelSide') }}</Button>
        <Button v-if="side.status === 'failed'" size="sm" variant="secondary" :disabled="busy" @click="emit('retry', side)">{{ tc('retry') }}</Button>
        <Button v-if="side.status === 'completed'" size="sm" :disabled="busy" @click="emit('advance', side)">{{ t('novelPlay.comparison.useBaseline') }}</Button>
        <Button v-if="adoptable" size="sm" variant="secondary" :disabled="side.adopted || !canAdopt" :data-testid="`comparison-adopt-${side.side}`" @click="emit('adopt', side)">
          {{ side.adopted ? tc('adopted') : tc('adopt') }}
        </Button>
      </div>
    </footer>
  </section>
</template>

<style scoped>
.cmp-side { display: grid; gap: var(--drama-space-sm); min-width: 0; padding: var(--drama-space-md); border: 1px solid var(--drama-color-border); border-radius: var(--drama-radius-md); background: var(--drama-color-surface); }
.cmp-side__head { display: grid; grid-template-columns: auto minmax(0, 1fr) auto; align-items: center; gap: var(--drama-space-sm); }
.cmp-side__mark { display: inline-grid; place-items: center; width: 28px; height: 28px; border-radius: var(--drama-radius-full); background: var(--drama-color-surface-alt); font-weight: 600; }
.cmp-side__metrics { display: flex; flex-wrap: wrap; align-items: center; gap: var(--drama-space-lg); margin: 0; padding-block: var(--drama-space-sm); border-block: 1px solid var(--drama-color-border); }
.cmp-side__metrics div { display: grid; gap: var(--drama-space-xs); }
.cmp-side__metrics dt { color: var(--drama-color-text-muted); font-size: 12px; }
.cmp-side__metrics dd { margin: 0; font-size: 13px; font-variant-numeric: tabular-nums; }
.cmp-side__body { min-height: 12rem; }
.cmp-side__note { margin: 0; color: var(--drama-color-text-muted); font-size: 12px; }
.cmp-side__error { margin: 0; color: var(--drama-color-danger); font-size: 13px; }
.cmp-side__foot { display: flex; justify-content: space-between; align-items: center; gap: var(--drama-space-sm); }
.cmp-side__link { color: var(--drama-color-primary); font-size: 13px; }
.cmp-side__actions { display: flex; flex-wrap: wrap; gap: var(--drama-space-sm); }
.cmp-line--narration .au-line__text { font-style: italic; }
.cmp-line--action .au-line__text { color: var(--drama-color-text-muted); }
</style>
